/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDraggable } from 'react-use-draggable-scroll';
import OfficeSpace from '../officeMapLayout/space';
import MeetingRoom from '../officeMapLayout/meetingRoom';
import { User } from '../../../store/reducers/user';
import { useTelegram } from '../../../hooks/useTelegram';
import { officeSpaceApi } from '../../../api/officeSpaceApi';
import { OfficeSpaceListResponse } from '../../../api/types';
import { FullPageLoader } from '../../common/FullPageLoader/FullPageLoader';
import styles from './styles';

export type MapConfig = {
    id: string;
    options: {
        disabled?: boolean;
        isBooked?: boolean;
        isMyBook?: boolean;
        render?: React.ReactNode | string;
        spaceTeams?: string[] | null;
    };
}[];

interface OfficeLayoutScreenProps {
    config?: MapConfig;
    setEditWorkPlaceActive: React.Dispatch<React.SetStateAction<boolean>>;
    setModalActive: React.Dispatch<React.SetStateAction<boolean>>;
    editWorkPlaceActive: boolean;
    chosenMeetingRoomDate?: string;
    isMeetingRoom: boolean;
    setIsMyBooking: React.Dispatch<React.SetStateAction<boolean>>;
    startDateState?: string;
    endDateState?: string;
    userInfo: User | null;
    setStartDateState: React.Dispatch<React.SetStateAction<string | undefined>>;
    setEndDateState: React.Dispatch<React.SetStateAction<string | undefined>>;
    setOtherUsersHaveBookingsModalActive: React.Dispatch<React.SetStateAction<boolean>>;
    setSpaceIdState: React.Dispatch<React.SetStateAction<string | null>>;
    setIsEditClosed: React.Dispatch<React.SetStateAction<boolean>>;
}

function OfficeLayoutScreen({
    config,
    setEditWorkPlaceActive,
    setModalActive,
    editWorkPlaceActive,
    chosenMeetingRoomDate,
    isMeetingRoom,
    setIsMyBooking,
    startDateState,
    endDateState,
    userInfo,
    setStartDateState,
    setEndDateState,
    setOtherUsersHaveBookingsModalActive,
    setSpaceIdState,
    setIsEditClosed,
}: OfficeLayoutScreenProps) {
    const { colorScheme } = useTelegram();
    const [mapConfig, setMapConfig] = useState<MapConfig | undefined>(config);

    const ref = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;

    const { events } = useDraggable(ref, {
        applyRubberBandEffect: true,
    });

    const [getOfficeSpaces, { isLoading: areOfficeSpacesLoading }] =
        officeSpaceApi.useLazyListQuery();

    const fetchOfficeSpaces = useCallback(async () => {
        const response = await getOfficeSpaces({}).unwrap();

        return response.data || [];
    }, []);

    useEffect(() => {
        const initializeMapConfig = async () => {
            const officeSpaces = await fetchOfficeSpaces();

            const initialMapConfig = mapResponseToMapConfig({ officeSpaces });

            setMapConfig(initialMapConfig);
        };

        setTimeout(() => {
            initializeMapConfig();
        }, 100);
    }, [fetchOfficeSpaces, config]);

    if (areOfficeSpacesLoading) {
        <FullPageLoader />;
    }

    return (
        <div {...events} ref={ref} css={styles.officeMapWrapperStyles({ colorScheme })}>
            <div css={styles.wrapperTopPartStyles}>
                <div css={styles.topManagementWrapperStyles}>
                    <div css={styles.topManagementRoomStyles({ colorScheme })}>
                        <p>TOP M.</p>
                    </div>
                    <MeetingRoom
                        roomName="MR1"
                        chosenMeetingRoomDate={chosenMeetingRoomDate}
                        isMeetingRoom={isMeetingRoom}
                    />
                </div>
                <div css={styles.enterStyles({ colorScheme })}>
                    <p className="enterText">Enter</p>
                </div>
                <div css={styles.wcStyles({ colorScheme })}>W/C</div>
                <div css={styles.kitchenLawAccWrapperStyles}>
                    <div css={styles.kitchenStyles({ colorScheme })}>Kitchen</div>
                    <div css={styles.lawAccStyles({ colorScheme })}>
                        <p>L&A</p>
                    </div>
                    {['MR2', 'MR3'].map((id) => (
                        <MeetingRoom
                            roomName={id}
                            chosenMeetingRoomDate={chosenMeetingRoomDate}
                            isMeetingRoom={isMeetingRoom}
                        />
                    ))}
                </div>
            </div>
            <div css={styles.wrapperBottomPartStyles}>
                <div css={styles.firstColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['37', '38', '39'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['36', '35', '34'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.secondColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['30', '31', '32', '33'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['29', '28', '27', '26'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.thirdColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['22', '23', '24', '25'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['21', '20', '19', '18'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.fourthColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['15', 'empty', '16', '17'].map((id) =>
                            id === 'empty' ? (
                                <div key="empty" css={styles.emptyRowStyles} />
                            ) : (
                                <OfficeSpace
                                    setModalActive={setModalActive}
                                    isMeetingRoom={isMeetingRoom}
                                    editWorkPlaceActive={editWorkPlaceActive}
                                    setIsMyBooking={setIsMyBooking}
                                    value={id}
                                    userInfo={userInfo}
                                    startDateState={startDateState}
                                    endDateState={endDateState}
                                    setOtherUsersHaveBookingsModalActive={
                                        setOtherUsersHaveBookingsModalActive
                                    }
                                    {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                                />
                            ),
                        )}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['14', '13', '12', '11'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.fifthColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['8', '9', '10'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['5', '6', '7'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                </div>
                <div css={styles.sixthColumnWrapperStyles}>
                    <div css={styles.spaceLeftColumnStyles}>
                        {['3', '4'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                    <div css={styles.spaceRightColumnStyles}>
                        {['2', '1'].map((id) => (
                            <OfficeSpace
                                setModalActive={setModalActive}
                                isMeetingRoom={isMeetingRoom}
                                editWorkPlaceActive={editWorkPlaceActive}
                                setIsMyBooking={setIsMyBooking}
                                value={id}
                                userInfo={userInfo}
                                startDateState={startDateState}
                                endDateState={endDateState}
                                setOtherUsersHaveBookingsModalActive={
                                    setOtherUsersHaveBookingsModalActive
                                }
                                {...retrieveOptionsFromConfig({ config: mapConfig, id })}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div css={styles.windowWrapperStyles}>
                <div css={styles.windowStyles({ colorScheme })}>Window</div>
            </div>
        </div>
    );
}

export default OfficeLayoutScreen;

function retrieveOptionsFromConfig({ config, id }: { config?: MapConfig; id?: string }) {
    const options = config?.find((record) => record.id === id);
    return options?.options;
}

function mapResponseToMapConfig({
    officeSpaces,
}: {
    officeSpaces: OfficeSpaceListResponse['data'];
}): MapConfig {
    const config: MapConfig = officeSpaces.map((officeSpace) => {
        return {
            id: officeSpace.name,
            options: {
                spaceTeams: officeSpace.bookingTeams?.map((team) => team),
            },
        };
    });

    return config;
}
